import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../hooks/useApi';
import EmbeddedCheckoutComponent from './EmbeddedCheckout';
import { plans } from '../data/plans';
import { getSubscriptionPlans, SubscriptionPlan } from '../services/credits';
import { API_URL } from '../config/apiConfig';

export default function CustomPricingTable() {
  const { user } = useAuth();
  const { user: userData } = useUser();
  const [loading, setLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [selectedPlanIdForCheckout, setSelectedPlanIdForCheckout] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [stripePlans, setStripePlans] = useState<Record<string, SubscriptionPlan>>({});
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Fetch Stripe plan prices on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await getSubscriptionPlans();
        const plansMap: Record<string, SubscriptionPlan> = {};
        response.plans.forEach((plan) => {
          plansMap[plan.id] = plan;
        });
        setStripePlans(plansMap);
      } catch (err) {
        console.error('Failed to fetch Stripe plans:', err);
        // Continue with default plans if fetch fails
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      alert('Please sign in to subscribe');
      return;
    }

    // Check if user has email (required for Stripe)
    if (!user.email) {
      alert('Please sign in with an email address to subscribe. Anonymous users cannot subscribe.');
      return;
    }

    setLoading(planId);

    try {
      // Plan IDs already match backend format (starter, pro, ultimate)
      const backendPlanId = planId;
      
      // Validate plan ID
      const validPlans = ['basic', 'pro', 'premium'];
      if (!validPlans.includes(backendPlanId)) {
        throw new Error(`Invalid plan: ${planId}. Please contact support.`);
      }

      // Create SetupIntent for PaymentElement (native form fields on your site)
      const response = await fetch(`${API_URL}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: backendPlanId,
          billing_period: billingPeriod,
          email: user.email,
          user_id: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create setup intent');
      }

      const data = await response.json();

      if (data.success && data.client_secret && data.customer_id) {
        // Store setup intent and customer info for PaymentElement
        setCheckoutClientSecret(data.client_secret);
        setCustomerId(data.customer_id);
        setSelectedPlanIdForCheckout(backendPlanId);
        
        // Store publishable key from backend response (if provided)
        if (data.publishable_key) {
          setStripePublishableKey(data.publishable_key);
        }
        
        setShowCheckout(true);
        // Scroll to checkout
        setTimeout(() => {
          document.getElementById('embedded-checkout')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        throw new Error('No setup intent client secret received from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error instanceof Error ? error.message : 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const isNetworkEnabled = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  return (
    <div className="w-full">
      <div className="auto-grid">
        {plans.map((plan) => {
          const stripePlan = stripePlans[plan.id];
          const isCurrentPlan = userData?.subscription_tier === plan.id;
          
          // Calculate display price from Stripe data
          const displayPrice = loadingPlans || !stripePlan
            ? null
            : stripePlan.price_monthly_usd;

          return (
            <div
              key={plan.id}
              className="bg-white/5 border border-white/20 rounded-xl p-6 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                {plan.popular && (
                  <span className="bg-blue-500/20 border border-blue-500/50 text-blue-400 px-2 py-0.5 rounded-full text-xs font-semibold">
                    Most Popular
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {loadingPlans || !stripePlan ? (
                  '...'
                ) : (
                  <>
                    ${displayPrice!.toFixed(0)}
                    <span className="text-sm font-normal text-white/60">/month</span>
                  </>
                )}
              </div>
              <p className="text-sm text-white/60 mb-4">
                Billed monthly
              </p>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="text-white/70 text-sm">
                    ✓ {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading === plan.id || !isNetworkEnabled || isCurrentPlan || loadingPlans || !stripePlan}
                className="w-full bg-white text-black py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading === plan.id
                  ? 'Loading...'
                  : isCurrentPlan
                  ? 'Current Plan'
                  : !isNetworkEnabled
                  ? 'Network Disabled'
                  : loadingPlans || !stripePlan
                  ? 'Loading...'
                  : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Embedded Checkout Modal */}
      {showCheckout && checkoutClientSecret && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => {
                setShowCheckout(false);
                setCheckoutClientSecret(null);
                setCustomerId(null);
                setSelectedPlanIdForCheckout(null);
                setStripePublishableKey(null);
                setLoading(null);
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Complete Your Subscription
            </h2>
            <div id="embedded-checkout">
              {checkoutClientSecret && customerId && selectedPlanIdForCheckout && user && (
                <EmbeddedCheckoutComponent
                  clientSecret={checkoutClientSecret}
                  customerId={customerId}
                  planId={selectedPlanIdForCheckout}
                  billingPeriod={billingPeriod}
                  publishableKey={stripePublishableKey || undefined}
                  userId={user.id}
                  onComplete={() => {
                    setShowCheckout(false);
                    setCheckoutClientSecret(null);
                    setCustomerId(null);
                    setSelectedPlanIdForCheckout(null);
                    setStripePublishableKey(null);
                    setLoading(null);
                    // Optionally reload or show success message
                    window.location.reload();
                  }}
                  onError={(error) => {
                    console.error('Checkout error:', error);
                    alert(error.message || 'Checkout failed. Please try again.');
                    setLoading(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

